import React, { useContext, useState } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
} from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../contexts/GlobalContext"
import { useQuery } from "@tanstack/react-query"
import { api } from "../../api"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomDatePicker from "../../components/CustomDatePicker"
import dayjs from "dayjs"

// Importación para generar íconos dinámicos según estándar
import getIconComponent from "../utils/getIconComponent"

import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

// Lista estática de estados de un Tomo
const listaEstados = [
  { id: "G", label: "GENERADO" },
  { id: "P", label: "EN PROCESO" },
  { id: "C", label: "CERRADO" },
  { id: "F", label: "FINALIZADO" },
  { id: "X", label: "ANULADO" },
]

const TomaFisicaIndex = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Controladores de estado visual
  // AJUSTE 1: Inicia cerrado (false) y con llave 0 para identificar el render inicial
  const [expanded, setExpanded] = useState(false)
  const [refreshTableKey, setRefreshTableKey] = useState(0)

  // Estado centralizado de filtros principales
  const [filtros, setFiltros] = useState({
    tomnumero: "",
    tomdescripcion: "",
    bodcodigo: "",
    tomstatus: "",
    fecDes: null,
    fecHas: null,
  })

  // Consulta de Catálogos (Bodegas) - Se dispara al abrir la pantalla
  const { data: rawCatalogos, isLoading: isLoadingCatalogos } = useQuery({
    queryKey: ["getCatalogosTomaFisica"],
    queryFn: async () => {
      try {
        const response = await api.post("/TomaFisica/getCatalogosTomaFisica", {})
        return response?.data?.data || response?.data || {}
      } catch (error) {
        console.error("Error al cargar catálogos:", error)
        return {}
      }
    },
    refetchOnWindowFocus: false,
  })

  // Extracción Defensiva de Arreglos para Combos
  const baseCatalogos = rawCatalogos || {}
  const rawBodegas = baseCatalogos?.bodegas || baseCatalogos?.data?.bodegas || []
  const listaBodegas = Array.isArray(rawBodegas) ? rawBodegas : []

  const handleInputChange = (field, value) => {
    setFiltros((prev) => ({ ...prev, [field]: value }))
  }

  const handleFiltrar = () => {
    // Al presionar buscar, la llave cambia, forzando a la tabla a recibir el endpoint real
    setRefreshTableKey((prev) => prev + 1)
    setExpanded(false)
  }

  // Helper para renderizar los colores de los estados
  const renderEstado = (status) => {
    const statusMap = {
      G: { text: "GENERADO", color: "#1976d2" },
      P: { text: "EN PROCESO", color: "#ed6c02" },
      C: { text: "CERRADO", color: "#9c27b0" },
      F: { text: "FINALIZADO", color: "#2e7d32" },
      X: { text: "ANULADO", color: "#d32f2f" },
    }
    const current = statusMap[status] || { text: status, color: "black" }

    return <span style={{ color: current.color, fontWeight: "bold" }}>{current.text}</span>
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Gestión de Tomas Físicas de Inventario</b>
        </div>

        <CustomBackdrop isLoading={isLoadingCatalogos} />

        <Box className={StyledRoot}>
          {/* ACORDEÓN DE FILTROS */}
          <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{ mb: 3, borderRadius: 2, boxShadow: 3, "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f5f7fa", borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Parámetros de Búsqueda
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Fila 1: Textos y Combos */}
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Número de Tomo"
                    size="small"
                    value={filtros.tomnumero}
                    onChange={(e) => handleInputChange("tomnumero", e.target.value.toUpperCase())}
                  />
                </Grid>
                <Grid item xs={12} sm={8} md={5}>
                  <TextField
                    fullWidth
                    label="Descripción del Tomo"
                    size="small"
                    value={filtros.tomdescripcion}
                    onChange={(e) => handleInputChange("tomdescripcion", e.target.value.toUpperCase())}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Autocomplete
                    options={listaBodegas}
                    getOptionLabel={(option) => option.label || ""}
                    value={listaBodegas.find((o) => o.id === filtros.bodcodigo) || null}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    onChange={(event, newValue) => {
                      handleInputChange("bodcodigo", newValue ? newValue.id : "")
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Bodega" size="small" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    select
                    fullWidth
                    label="Estado"
                    size="small"
                    value={filtros.tomstatus}
                    onChange={(e) => handleInputChange("tomstatus", e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todos</em>
                    </MenuItem>
                    {listaEstados.map((op) => (
                      <MenuItem key={op.id} value={op.id}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Fila 2: Fechas */}
                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    label="Fecha Planificación Desde"
                    value={filtros.fecDes}
                    setValue={(v) => handleInputChange("fecDes", v)}
                    isOptional={true}
                    format="DD/MM/YYYY"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <CustomDatePicker
                    label="Fecha Planificación Hasta"
                    value={filtros.fecHas}
                    setValue={(v) => handleInputChange("fecHas", v)}
                    isOptional={true}
                    format="DD/MM/YYYY"
                  />
                </Grid>

                {/* Botón de Acción */}
                <Grid item xs={12} display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SearchIcon />}
                    onClick={handleFiltrar}
                    sx={{ px: 4, fontWeight: "bold" }}
                  >
                    Ejecutar Búsqueda
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* TABLA DE RESULTADOS (Siempre renderizada en el DOM) */}
          <CustomConditionalActionsTableServer
            key={refreshTableKey}
            // AJUSTE 2: Condicionamos el endpoint. Si es la carga inicial (0), se envía vacío para anular el Axios fetch.
            endpoint={refreshTableKey === 0 ? "" : "/TomaFisica/getAllTomosGenerados"}
            endpointJson={{
              externalFilters: {
                ...filtros,
                fecDes: filtros.fecDes ? dayjs(filtros.fecDes).format("YYYY-MM-DD") : "",
                fecHas: filtros.fecHas ? dayjs(filtros.fecHas).format("YYYY-MM-DD") : "",
              },
            }}
            errorMsgFilterSearch="Error al cargar los tomos físicos."
            queryKeyModal="TomaFisicaData"
            perPage={15}
            rowActionsWidthTable={130}
            rowActions={(row) => {
              const acciones = []

              const buscarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "BUSCAR",
              )
              if (buscarAction) {
                acciones.push({
                  label: "Consultar",
                  key: "BUSCAR",
                  icon: getIconComponent(buscarAction?.accnameicono, buscarAction?.acctipoico),
                  onClick: (row) =>
                    navigate("buscar", {
                      state: { tomnumero: row.original.tomnumero },
                    }),
                })
              }

              const detalleAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "DETALLE",
              )
              if (detalleAction) {
                acciones.push({
                  label: "Auditoría / Detalle",
                  key: "DETALLE",
                  icon: getIconComponent(detalleAction?.accnameicono, detalleAction?.acctipoico),
                  onClick: (row) =>
                    navigate("detalle", {
                      state: { tomnumero: row.original.tomnumero },
                    }),
                })
              }

              return acciones
            }}
            topToolbarCustomActions={({ table, device }) => {
              const crearAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action.acccaption === "CREAR")

              const toolbarActions = []
              if (crearAction) {
                toolbarActions.push({
                  label: crearAction?.acccaption,
                  key: crearAction?.acccaption,
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => navigate("crear"),
                })
              }
              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "tomnumero",
                header: "No. Tomo",
                size: 160,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "tomdescripcion",
                header: "Descripción",
                size: 250,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "tomstatus",
                header: "Estado",
                size: 130,
                Cell: ({ cell }) => renderEstado(cell.getValue()),
              },
              {
                accessorKey: "bodcodigo",
                header: "Bodega",
                size: 100,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "tomtipo",
                header: "Tipo",
                size: 110,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "tomfecha",
                header: "Fecha Plan.",
                size: 110,
                Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
              },
              {
                accessorKey: "tomusuisys",
                header: "Resp. Planificación",
                size: 150,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "tomfecisys",
                header: "Fec. Sistema",
                size: 110,
                Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default TomaFisicaIndex
